'use client'

export const dynamic = 'force-dynamic'

import { Suspense, useEffect, useMemo, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { toast } from '@warfighter/ui'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import { Input } from '@/components/ui/input'
import { checkRateLimit } from '@/lib/rate-limit'

function LoginContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [oauthLoading, setOauthLoading] = useState<'google' | 'apple' | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetMessage, setResetMessage] = useState('')
  const [resetError, setResetError] = useState('')

  const nextParam = searchParams.get('next')
  const defaultParams = useMemo(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('next')
    return params.toString()
  }, [searchParams])
  const onboardingUrl = defaultParams ? `/onboarding?${defaultParams}` : '/onboarding'

  const setAccessCookie = () => {
    document.cookie = `wm_access=1; path=/; max-age=${60 * 60 * 24 * 7}; samesite=lax`
  }

  /** Decide where to send the user after auth succeeds. */
  const resolveRedirect = async (supabase: ReturnType<typeof createBrowserSupabaseClient>) => {
    // If the caller explicitly requested a destination (e.g. ?next=/dashboard), honour it.
    if (nextParam) return nextParam

    // Check whether onboarding is already done (and whether user is a tester).
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('onboarding_complete, role')
        .single()

      const isTester = profile?.role === 'tester'
      const onboardingDone = profile?.onboarding_complete === true

      // Testers always go to onboarding so they can re-test the flow.
      if (isTester) return onboardingUrl

      // Completed users go straight to the dashboard.
      if (onboardingDone) return '/dashboard'
    } catch {
      // If the profile query fails (e.g. new user, no row yet), fall through to onboarding.
    }

    return onboardingUrl
  }

  useEffect(() => {
    const supabase = createBrowserSupabaseClient()
    supabase.auth.getSession().then(async ({ data }) => {
      if (data.session) {
        setAccessCookie()
        const dest = await resolveRedirect(supabase)
        router.replace(dest)
      }
    })
  }, [router]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOAuth = async (provider: 'google' | 'apple') => {
    setOauthLoading(provider)
    const supabase = createBrowserSupabaseClient()
    // OAuth callback lands back on /login which then runs resolveRedirect
    await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/login`
      }
    })
    setOauthLoading(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      checkRateLimit('login')
    } catch (err) {
      toast.warning((err as Error).message)
      return
    }
    setLoading(true)

    const supabase = createBrowserSupabaseClient()
    const { error } = await supabase.auth.signInWithPassword({ email, password })

    setLoading(false)
    if (error) {
      toast.error(error.message)
      return
    }

    setAccessCookie()
    const dest = await resolveRedirect(supabase)
    router.push(dest)
  }

  const handleResetPassword = async () => {
    try {
      checkRateLimit('password-reset')
    } catch (err) {
      toast.warning((err as Error).message)
      return
    }
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      setResetError('Enter your email above to receive a reset link.')
      setResetMessage('')
      return
    }
    setResetError('')
    setResetMessage('')
    const supabase = createBrowserSupabaseClient()
    const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
      redirectTo: `${window.location.origin}/reset-password`
    })
    if (error) {
      setResetError(error.message)
      return
    }
    setResetMessage('Check your email for a reset link.')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-10">
        <h1 className="text-2xl font-bold text-slate-900">Welcome back</h1>
        <p className="mt-2 text-slate-500">Pick up your plan and keep moving forward.</p>

        <div className="mt-6 space-y-3">
          <button
            type="button"
            onClick={() => handleOAuth('google')}
            disabled={oauthLoading !== null}
            className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            {oauthLoading === 'google' ? (
              <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            )}
            Continue with Google
          </button>

          {/* TODO: Apple OAuth — defer to post-launch */}
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Or continue with email
          </span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-600">Email address</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={handleResetPassword}
              className="mt-2 text-xs font-semibold text-blue-600 hover:underline"
            >
              Forgot password?
            </button>
            {resetMessage ? (
              <p className="mt-2 text-xs text-emerald-600">{resetMessage}</p>
            ) : null}
            {resetError ? (
              <p className="mt-2 text-xs text-rose-600">{resetError}</p>
            ) : null}
          </div>
          <button
            type="submit"
            disabled={loading}
            className="flex h-12 w-full items-center justify-center rounded-full bg-[var(--accent)] text-sm font-semibold text-[var(--accent-contrast)] shadow-lg shadow-[var(--accent)]/20 transition hover:bg-[var(--accent-hover)] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log In'}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          Need an account?{' '}
          <button
            type="button"
            onClick={() => router.push(searchParams.toString() ? `/signup?${searchParams}` : '/signup')}
            className="font-semibold text-blue-600 hover:underline"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
          <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl shadow-blue-100 md:p-10">
            <div className="h-4 w-32 rounded bg-slate-200" />
            <div className="mt-3 h-3 w-48 rounded bg-slate-100" />
          </div>
        </div>
      }
    >
      <LoginContent />
    </Suspense>
  )
}
